import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import client from '../api/client'
import { useAuthStore } from '../store/authStore'
import { toast } from '../components/Toast'

const CATEGORIES = ['문의', '건의', '버그신고'] as const
type Category = typeof CATEGORIES[number]

const CATEGORY_ICONS: Record<Category, string> = {
  '문의': '❓',
  '건의': '💡',
  '버그신고': '🐛',
}

export default function WriteInquiry() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const [submitting, setSubmitting] = useState(false)
  const [category, setCategory] = useState<Category>('문의')
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true })
    }
  }, [isAuthenticated])

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast('제목을 입력해주세요.', 'error')
      return
    }
    if (!content.trim()) {
      toast('내용을 입력해주세요.', 'error')
      return
    }
    setSubmitting(true)
    try {
      await client.post('/inquiries', {
        category,
        title: title.trim(),
        content: content.trim(),
      })
      toast('문의가 등록되었습니다.', 'success')
      navigate('/inquiries')
    } catch (e: any) {
      toast(e?.response?.data?.detail || '등록에 실패했습니다.', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  const inputCls =
    'w-full bg-[var(--color-bg)] border border-[var(--color-border-default)] rounded-lg px-4 py-2.5 text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-border-accent)] transition-colors text-sm resize-none'

  return (
    <div className="min-h-screen bg-[var(--color-bg)] p-4 sm:p-6">
      <div className="max-w-lg mx-auto space-y-4">
        {/* 헤더 */}
        <div className="flex items-center gap-3 mb-2">
          <button
            onClick={() => navigate(-1)}
            className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] transition-colors p-1"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-xl font-bold text-[var(--color-text-primary)]">문의 / 건의</h1>
            <p className="text-[var(--color-text-tertiary)] text-xs">궁금한 점이나 불편한 사항을 알려주세요</p>
          </div>
        </div>

        {/* 카테고리 */}
        <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5">
          <h2 className="text-[var(--color-text-primary)] font-semibold mb-4">📂 유형 선택</h2>
          <div className="flex gap-2">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setCategory(cat)}
                className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition-colors ${
                  category === cat
                    ? 'bg-[var(--color-accent)] border-[var(--color-accent)] text-[var(--color-primary)]'
                    : 'bg-[var(--color-bg)] border-[var(--color-border-default)] text-[var(--color-text-tertiary)] hover:border-[var(--color-border-accent)] hover:text-[var(--color-text-primary)]'
                }`}
              >
                {CATEGORY_ICONS[cat]} {cat}
              </button>
            ))}
          </div>
        </div>

        {/* 제목 */}
        <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5">
          <h2 className="text-[var(--color-text-primary)] font-semibold mb-4">📝 제목</h2>
          <input
            type="text"
            className={inputCls}
            placeholder="제목을 입력해주세요."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={100}
          />
          <p className="text-right text-xs text-[var(--color-text-disabled)] mt-1">{title.length} / 100</p>
        </div>

        {/* 내용 */}
        <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5">
          <h2 className="text-[var(--color-text-primary)] font-semibold mb-4">✏️ 내용</h2>
          <textarea
            className={inputCls}
            rows={6}
            placeholder="내용을 자세히 작성해 주세요."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            maxLength={2000}
          />
          <p className="text-right text-xs text-[var(--color-text-disabled)] mt-1">{content.length} / 2000</p>
        </div>

        {/* 버튼 */}
        <div className="flex gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex-1 py-3 border border-[var(--color-border-default)] text-[var(--color-text-tertiary)] rounded-xl text-sm hover:text-[var(--color-text-primary)] transition-colors"
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 py-3 bg-[var(--color-accent)] hover:opacity-90 disabled:opacity-50 text-[var(--color-primary)] rounded-xl font-bold text-sm transition-opacity"
          >
            {submitting ? '등록 중...' : '등록하기'}
          </button>
        </div>
      </div>
    </div>
  )
}
